/**
 * MAINT-FORM-112 — carry an item from one pricing screen to another.
 *
 * The legacy forms handed off through a parameter list and an AUTO QUERY
 * startup mode, so the target opened already showing the item you were looking
 * at (prc_promo_prc_bnr.txt:37782-37806, open_reg_prc_bnr_form):
 *
 *   CG$STARTUP_MODE = 'AUTO QUERY'
 *   P_RITEM_NO, P_UPC, P_WHITEM_NO, P_SYS_CD, P_SPLR_NO
 *
 * A URL is this application's parameter list. The names are kept close to the
 * legacy ones so the mapping is obvious to anyone holding the old form open
 * beside the new screen.
 *
 * A link that lands you on the right screen but empty is worse than no link:
 * it looks like the hand-off worked and quietly makes you type the item again.
 * So the target screens read these and run the search themselves.
 */
export interface ItemLinkContext {
  /**
   * Every supplier ticked on the sending screen, and whether its Warehouse box
   * was ticked. `splrNo` still carries the first supplier for targets that take
   * one; Display Price Details reads these to tick the same set.
   */
  splrNos?: number[] | null;
  warehouse?: boolean | null;
  /** Which key the sender searched by, so the receiver opens on the same one. */
  key?: "upc" | "ritemNo" | "whitemNo" | null;
  ritemNo?: number | null;
  upc?: number | string | null;
  whitemNo?: number | null;
  sysCd?: string | null;
  splrNo?: number | null;
  /**
   * The four extra parameters the Scanback hand-off carries
   * (PRC_ADD_PNDG_PROMO.txt:30807-30814, go_scanback_maint). Scanback
   * Maintenance is scoped to a banner and a date range, not just an item, and
   * P_FMLY_FG tells it whether the promotion it is following priced the whole
   * family. Optional, so the item-only hand-offs are unchanged.
   */
  bnrCd?: string | null;
  startDate?: string | null;
  endDate?: string | null;
  allFamily?: boolean | null;
  /**
   * The promotion just created, for Model after Promotion
   * (PRC_ADD_PNDG_PROMO.txt:30437, create_scan_grp). Legacy passed the
   * promotion's zone/override/store shape in a Forms record group; the code
   * plus the start date find those same rows on RETAIL_PRICE.
   */
  promCd?: number | null;
  /**
   * The perspective a caller can hand over. PRC_DISP_PRC_DETAILS accepts eight
   * inbound parameters (P_SPLR_NO, P_SYS_CD, P_WHITEM_NO, P_UPC, P_RITEM_NO,
   * P_BNR_CD, P_ZN_NO, P_STR_NO); these two were the ones the link vocabulary
   * did not carry. They matter for that screen in particular, which refuses to
   * query without a banner AND a zone-or-store - so a hand-off carrying only the
   * item lands on a form that is filled in but cannot run.
   */
  znNo?: number | null;
  strNo?: number | null;
  /**
   * The route of the screen that sent the hand-off, so the target can link
   * back with the same context (ReturnLink). Only the shared hand-off bar sets
   * it; the receiver checks it against the known senders before using it.
   */
  from?: string | null;
}

/** Build the query string for a hand-off. Empty values are omitted, not sent blank. */
export const itemLinkQuery = (ctx: ItemLinkContext): string => {
  const p = new URLSearchParams();
  if (ctx.ritemNo != null) p.set("ritemNo", String(ctx.ritemNo));
  if (ctx.upc != null && String(ctx.upc).trim() !== "") p.set("upc", String(ctx.upc));
  if (ctx.whitemNo != null) p.set("whitemNo", String(ctx.whitemNo));
  if (ctx.sysCd) p.set("sysCd", ctx.sysCd);
  if (ctx.splrNo != null) p.set("splrNo", String(ctx.splrNo));
  if (ctx.splrNos && ctx.splrNos.length > 0) p.set("splrNos", ctx.splrNos.join(","));
  if (ctx.warehouse) p.set("wh", "Y");
  if (ctx.key) p.set("key", ctx.key);
  if (ctx.bnrCd) p.set("bnrCd", ctx.bnrCd);
  if (ctx.startDate) p.set("startDate", ctx.startDate);
  if (ctx.endDate) p.set("endDate", ctx.endDate);
  // Only ever sent as "Y": legacy's P_FMLY_FG is 'N' by default, and an absent
  // parameter and an 'N' one mean the same thing to the target.
  if (ctx.allFamily) p.set("fmlyFg", "Y");
  if (ctx.promCd != null) p.set("promCd", String(ctx.promCd));
  if (ctx.znNo != null) p.set("znNo", String(ctx.znNo));
  if (ctx.strNo != null) p.set("strNo", String(ctx.strNo));
  if (ctx.from) p.set("from", ctx.from);
  return p.toString();
};

/** `path?ritemNo=…&upc=…`, or just `path` when there is no context to carry. */
export const itemLinkTo = (path: string, ctx: ItemLinkContext): string => {
  const q = itemLinkQuery(ctx);
  return q ? `${path}?${q}` : path;
};

/** Options for readItemLink. */
export interface ReadItemLinkOptions {
  /**
   * Count a Retail ID (`ritemNo`) on its own as a usable arrival.
   *
   * Off by default, and that default is not laziness: most screens' search
   * boxes take a UPC or a whitem, so treating a bare ritem as an arrival would
   * make them announce "arrived from a link" and then fail to search, which is
   * worse than ignoring the link.
   *
   * Regular Pricing by Banner opts in, because it now HAS a Retail ID search
   * key -- the legacy ITEMS block's third queryable column
   * (PRC_REG_PRC_BNR.txt:9015). Turn this on for a screen only once it can
   * actually search by ritem alone.
   */
  acceptRitemOnly?: boolean;
}

/**
 * Read a hand-off out of a query string.
 *
 * Returns null when nothing usable is present, so a screen can tell "arrived
 * from a link" apart from "opened normally" and leave its own restored session
 * state alone in the second case.
 */
const isSupplierNo = (n: number) => Number.isFinite(n) && n > 0;

export const readItemLink = (
  search: string,
  options: ReadItemLinkOptions = {}
): ItemLinkContext | null => {
  const p = new URLSearchParams(search);
  const num = (k: string): number | null => {
    const raw = p.get(k);
    if (raw == null || raw.trim() === "") return null;
    const n = Number(raw);
    return Number.isFinite(n) ? n : null;
  };

  const ctx: ItemLinkContext = {
    ritemNo: num("ritemNo"),
    upc: p.get("upc"),
    whitemNo: num("whitemNo"),
    sysCd: p.get("sysCd"),
    splrNo: num("splrNo"),
    bnrCd: p.get("bnrCd"),
    startDate: p.get("startDate"),
    endDate: p.get("endDate"),
    allFamily: p.get("fmlyFg") === "Y",
    promCd: num("promCd"),
    znNo: num("znNo"),
    strNo: num("strNo"),
    splrNos: (p.get("splrNos") ?? "")
      .split(",")
      .map((x) => Number(x.trim()))
      .filter((n) => isSupplierNo(n)),
    warehouse: p.get("wh") === "Y",
    key: (["upc", "ritemNo", "whitemNo"] as const).find((k) => k === p.get("key")) ?? null,
  };

  // A UPC or a whitem number is what most search boxes take. A ritem number
  // counts too, but only for a caller that says it can search by one --
  // see ReadItemLinkOptions.acceptRitemOnly.
  const usable =
    (ctx.upc != null && String(ctx.upc).trim() !== "")
    || ctx.whitemNo != null
    || (!!options.acceptRitemOnly && ctx.ritemNo != null);
  return usable ? ctx : null;
};
